<template>
  <div class="marketing-shell">
    <MarketingHeader />
    <div v-if="page" ref="content" class="marketing-page-content" @click="handleClick" @input="handleInput" @submit.prevent="handleSubmit">
      <MarketingContentNode v-for="(node, index) in page.content" :key="`${page.slug}-${index}`" :node="node" />
    </div>
    <main v-else id="main" class="marketing-page-loading container" aria-live="polite">Loading page…</main>
    <MarketingFooter />

    <button class="scroll-top-btn" :class="{ 'is-visible': showScrollTop }" type="button" aria-label="Scroll to top" @click="scrollToTop">↑</button>
    <PublicFloatingCtas />

    <div v-if="cookieVisible" class="cookie-banner is-visible" role="dialog" aria-label="Cookie notice">
      <p>We use cookies to improve your experience and analyze site traffic. See our <router-link to="/privacy">Privacy Policy</router-link>.</p>
      <div class="cookie-actions">
        <button class="btn btn-secondary btn-sm" type="button" @click="setCookieConsent('declined')">Decline</button>
        <button class="btn btn-primary btn-sm" type="button" @click="setCookieConsent('accepted')">Accept</button>
      </div>
    </div>

    <div class="preview-modal" :class="{ 'is-open': preview.open }" role="dialog" aria-modal="true" aria-label="Asset preview">
      <button class="preview-modal-backdrop" type="button" aria-label="Close preview" @click="closePreview"></button>
      <div class="preview-modal-panel">
        <div class="preview-modal-head">
          <span class="preview-modal-title">{{ preview.title }}</span>
          <button type="button" class="preview-modal-close" aria-label="Close preview" @click="closePreview">×</button>
        </div>
        <div class="preview-modal-body">
          <img v-if="preview.src" class="preview-modal-img" :src="preview.src" :alt="preview.title" />
          <div v-else class="preview-modal-empty">
            <span class="preview-empty-icon" aria-hidden="true">▧</span>
            <p>These items are available by emailing <a href="mailto:sales@ucari.com">sales@ucari.com</a> (please refer to the item in question).</p>
          </div>
        </div>
        <div class="preview-modal-foot">
          <a v-if="preview.src" class="btn btn-primary btn-sm preview-modal-download" :href="preview.src" download>Download</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import { marketingManifest } from '@/content/marketingManifest';
import MarketingContentNode from '@/views/components/MarketingContentNode.vue';
import MarketingFooter from '@/views/components/MarketingFooter.vue';
import MarketingHeader from '@/views/components/MarketingHeader.vue';
import PublicFloatingCtas from '@/views/components/PublicFloatingCtas.vue';

const ingredientData = {
  dairy: { status: 'flag', label: 'Common Intolerance', note: 'Frequently flagged in human sensitivity panels.' },
  gluten: { status: 'flag', label: 'Common Intolerance', note: 'Frequently flagged in human sensitivity panels.' },
  chicken: { status: 'ok', label: 'Low Reactivity', note: 'Rarely flagged; a common novel protein alternative.' },
  beef: { status: 'flag', label: 'Common Intolerance', note: 'One of the most frequently flagged pet proteins.' },
  salmon: { status: 'ok', label: 'Low Reactivity', note: 'Often well tolerated and rich in omega-3s.' },
  soy: { status: 'flag', label: 'Common Intolerance', note: 'Frequently flagged in both human and pet panels.' },
  oats: { status: 'ok', label: 'Low Reactivity', note: 'Generally well tolerated in moderation.' },
  egg: { status: 'flag', label: 'Common Intolerance', note: 'A frequently flagged ingredient across panels.' },
};

export default {
  name: 'MarketingPage',
  components: { MarketingContentNode, MarketingFooter, MarketingHeader, PublicFloatingCtas },
  props: {
    pagePath: { type: String, required: true },
  },
  data() {
    return {
      showScrollTop: false,
      page: null,
      loadSequence: 0,
      cookieVisible: false,
      cookieTimer: null,
      preview: { open: false, src: '', title: 'Preview' },
    };
  },
  computed: {
    pageMeta() { return marketingManifest[this.pagePath]; },
  },
  watch: {
    pagePath() { this.loadPage(); },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('keydown', this.onKeydown);
    if (!localStorage.getItem('ucari_cookie_consent')) {
      this.cookieTimer = window.setTimeout(() => { this.cookieVisible = true; }, 900);
    }
    this.loadPage();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('keydown', this.onKeydown);
    window.clearTimeout(this.cookieTimer);
    document.body.style.overflow = '';
  },
  methods: {
    async loadPage() {
      const sequence = ++this.loadSequence;
      this.page = null;
      document.title = this.pageMeta.title;
      this.setDescription(this.pageMeta.description);
      const response = await fetch(this.pageMeta.contentUrl);
      if (!response.ok) throw new Error(`Unable to load ${this.pageMeta.contentUrl}`);
      const page = await response.json();
      if (sequence !== this.loadSequence) return;
      this.page = page;
      await nextTick();
      const root = this.$refs.content;
      if (!root) return;
      root.querySelectorAll('.reveal, .timeline-item').forEach((element) => element.classList.add('in-view'));
      root.querySelectorAll('[data-count]').forEach((element) => {
        const number = Number(element.getAttribute('data-count'));
        const suffix = element.getAttribute('data-suffix') || '';
        element.textContent = `${Number.isInteger(number) ? number.toLocaleString('en-US') : number}${suffix}`;
      });
      this.calculateRevenue();
      await nextTick();
      if (this.$route.hash) {
        document.querySelector(this.$route.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0 });
      }
    },
    setDescription(description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = description || '';
    },
    onScroll() { this.showScrollTop = window.scrollY > 480; },
    onKeydown(event) { if (event.key === 'Escape') this.closePreview(); },
    scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }); },
    setCookieConsent(value) {
      localStorage.setItem('ucari_cookie_consent', value);
      this.cookieVisible = false;
    },
    handleClick(event) {
      const accordionHeader = event.target.closest('.accordion-header');
      if (accordionHeader) {
        const item = accordionHeader.closest('.accordion-item');
        const accordion = accordionHeader.closest('.accordion');
        const wasOpen = item.classList.contains('is-open');
        if (accordion.hasAttribute('data-single-open')) {
          accordion.querySelectorAll('.accordion-item').forEach((other) => this.setAccordion(other, false));
        }
        this.setAccordion(item, !wasOpen);
        return;
      }

      const previewTrigger = event.target.closest('[data-preview-trigger]');
      if (previewTrigger) {
        event.preventDefault();
        this.openPreview(previewTrigger.getAttribute('data-preview-src'), previewTrigger.getAttribute('data-preview-title'));
        return;
      }

      if (event.target.closest('.preview-modal-close, .preview-modal-backdrop')) {
        this.closePreview();
        return;
      }

      const carouselButton = event.target.closest('.carousel-arrow');
      if (carouselButton) {
        this.moveCarousel(carouselButton.closest('.testimonial-carousel'), carouselButton.classList.contains('next') ? 1 : -1);
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || link.target === '_blank' || link.hasAttribute('download')) return;
      const href = link.getAttribute('href');
      if (href?.startsWith('/')) {
        event.preventDefault();
        this.$router.push(href);
      }
    },
    setAccordion(item, open) {
      item.classList.toggle('is-open', open);
      const header = item.querySelector('.accordion-header');
      const panel = item.querySelector('.accordion-panel');
      header?.setAttribute('aria-expanded', String(open));
      if (panel) panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '';
    },
    handleInput(event) {
      if (event.target.id === 'ingredient-search') this.updateIngredient(event.target.value);
      if (['calc-customers', 'calc-acceptance', 'calc-retest'].includes(event.target.id)) this.calculateRevenue();
    },
    updateIngredient(value) {
      const result = this.$refs.content?.querySelector('#ingredient-result');
      if (!result) return;
      const query = value.trim().toLowerCase();
      if (!query) {
        result.replaceChildren();
        return;
      }
      const key = Object.keys(ingredientData).find((item) => item.startsWith(query));
      if (!key) {
        const note = document.createElement('p');
        note.className = 'form-note';
        note.textContent = 'No exact match — try dairy, gluten, beef, soy, egg, salmon, chicken, or oats in this demo.';
        result.replaceChildren(note);
        return;
      }
      const data = ingredientData[key];
      const card = document.createElement('div');
      card.className = `ingredient-result-card ${data.status}`;
      const title = document.createElement('strong');
      title.textContent = key.charAt(0).toUpperCase() + key.slice(1);
      const tag = document.createElement('span');
      tag.className = 'tag';
      tag.textContent = data.label;
      const note = document.createElement('p');
      note.textContent = data.note;
      card.append(title, tag, note);
      result.replaceChildren(card);
    },
    calculateRevenue() {
      const root = this.$refs.content;
      const customers = root?.querySelector('#calc-customers');
      const acceptance = root?.querySelector('#calc-acceptance');
      const retest = root?.querySelector('#calc-retest');
      if (!customers || !acceptance || !retest) return;
      const customerCount = Math.max(0, Number(customers.value) || 0);
      const acceptanceRate = (Number(acceptance.value) || 0) / 100;
      const retestRate = (Number(retest.value) || 0) / 100;
      const kits = customerCount * acceptanceRate;
      const kitRevenue = kits * 40;
      const spendRevenue = kits * 300;
      const yearOne = kitRevenue + spendRevenue;
      const retestRevenue = kits * retestRate * 40;
      const steady = yearOne + retestRevenue;
      const values = {
        'calc-acceptance-val': `${(acceptanceRate * 100).toFixed(1)}%`,
        'calc-retest-val': `${Math.round(retestRate * 100)}%`,
        'calc-kits': Math.round(kits).toLocaleString('en-US'),
        'calc-kit-revenue': this.currency(kitRevenue),
        'calc-spend-revenue': this.currency(spendRevenue),
        'calc-year1': this.currency(yearOne),
        'calc-retest-revenue': this.currency(retestRevenue),
        'calc-steady': this.currency(steady),
        'calc-3yr': this.currency(yearOne + steady * 2),
      };
      Object.entries(values).forEach(([id, text]) => {
        const element = root.querySelector(`#${id}`);
        if (element) element.textContent = text;
      });
    },
    currency(value) { return `$${Math.round(value).toLocaleString('en-US')}`; },
    async handleSubmit(event) {
      const form = event.target;
      if (form.matches('form[data-contact-form]')) {
        await this.submitContactForm(form);
        return;
      }
      if (!form.matches('form[data-demo-form]')) return;
      form.style.display = 'none';
      const success = form.parentElement?.querySelector('.form-success');
      if (success) success.style.display = 'block';
    },
    async submitContactForm(form) {
      if (!form.reportValidity()) return;

      const button = form.querySelector('button[type="submit"]');
      const error = form.parentElement?.querySelector('.form-error');
      const originalLabel = button?.textContent || 'Send Message';
      if (error) {
        error.textContent = '';
        error.style.display = 'none';
      }
      if (button) {
        button.disabled = true;
        button.textContent = 'Sending…';
      }

      const values = Object.fromEntries(new FormData(form).entries());
      try {
        const base = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${base}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: values.name,
            email: values.email,
            company: values.company,
            inquiryType: values['inquiry-type'],
            message: values.message,
            website: values.website,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Unable to send your message.');

        form.reset();
        form.style.display = 'none';
        const success = form.parentElement?.querySelector('.form-success');
        if (success) success.style.display = 'block';
      } catch (submissionError) {
        if (error) {
          error.textContent = submissionError.message || 'Unable to send your message. Please try again.';
          error.style.display = 'block';
        }
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      }
    },
    openPreview(src, title) {
      this.preview = { open: true, src: src || '', title: title || 'Preview' };
      document.body.style.overflow = 'hidden';
    },
    closePreview() {
      this.preview.open = false;
      document.body.style.overflow = '';
    },
    moveCarousel(carousel, delta) {
      if (!carousel) return;
      const slides = carousel.querySelectorAll('.testimonial-card');
      const track = carousel.querySelector('.testimonial-track');
      if (!slides.length || !track) return;
      const current = Number(carousel.dataset.vueIndex || 0);
      const next = (current + delta + slides.length) % slides.length;
      carousel.dataset.vueIndex = String(next);
      track.style.transform = `translateX(-${next * 100}%)`;
    },
  },
};
</script>
